import { useState, useEffect } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Calculator, Calendar, Clock, IndianRupee, ArrowRight, Check } from 'lucide-react';
import MagneticButton from '../components/MagneticButton';

const servicesOptions = [
  { id: 'design', label: 'UI/UX Design', price: 15000, time: 5, description: 'Premium visual prototype & layouts' },
  { id: 'development', label: 'Web Development', price: 25000, time: 7, description: 'High-performance React code' },
  { id: 'ecommerce', label: 'E-commerce Features', price: 20000, time: 6, description: 'Shopping cart & secure payments' },
  { id: 'cms', label: 'Content Management (CMS)', price: 10000, time: 3, description: 'Sanity or WordPress admin integration' },
  { id: 'animations', label: 'Custom 3D & GSAP Animations', price: 15000, time: 4, description: 'WebGL & custom spring transitions' },
  { id: 'ai', label: 'AI Core Integration', price: 25000, time: 5, description: 'LLM agents & prompt automation' }
];

const AnimatedNumber = ({ value }) => {
  const springValue = useSpring(value, { stiffness: 100, damping: 15 });
  const displayValue = useTransform(springValue, (latest) => Math.floor(latest));
  const [currentVal, setCurrentVal] = useState(value);

  useEffect(() => {
    springValue.set(value);
  }, [value, springValue]);

  useEffect(() => {
    return displayValue.onChange((v) => setCurrentVal(v));
  }, [displayValue]);

  return <span>{currentVal.toLocaleString()}</span>;
};

const ProjectEstimator = () => {
  const [selectedServices, setSelectedServices] = useState(['design', 'development']);
  const [pages, setPages] = useState(3);
  const [isUrgent, setIsUrgent] = useState(false);
  const navigate = useNavigate();

  // Price calculations
  const calculateTotal = () => {
    let serviceTotal = selectedServices.reduce((acc, curr) => {
      const option = servicesOptions.find((opt) => opt.id === curr);
      return acc + (option ? option.price : 0);
    }, 0);

    // Page scaling
    const pageCost = (pages - 1) * 2000;
    let grandTotal = serviceTotal + pageCost;

    // Urgency markup (25%)
    if (isUrgent) {
      grandTotal *= 1.25;
    }

    return Math.floor(grandTotal);
  };

  // Time calculations
  const calculateTime = () => {
    let totalTime = selectedServices.reduce((acc, curr) => {
      const option = servicesOptions.find((opt) => opt.id === curr);
      return acc + (option ? option.time : 0);
    }, 0);

    // Add extra time per page
    totalTime += Math.floor(pages * 0.5);

    // Urgent schedule reduces delivery time by 30%
    if (isUrgent) {
      totalTime *= 0.7;
    }

    return Math.max(3, Math.floor(totalTime));
  };

  const handleServiceToggle = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBookingRedirect = () => {
    // Generate text message summarizing estimate selection
    const summary = `I estimated a project cost of ₹${calculateTotal()} for: ${selectedServices.map(s => servicesOptions.find(o => o.id === s)?.label).join(', ')} (${pages} pages).`;
    navigate('/contact', { state: { projectSummary: summary } });
  };

  const estimatorSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Elvyen Project Cost Estimator",
    "url": "https://elvyen.com/estimator",
    "description": "Interactive project cost and development time calculator for web projects in India.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "All"
  };

  return (
    <>
      <SEO 
        title="Project Budget Estimator | Web Design Calculator" 
        description="Calculate your project cost instantly with our interactive budget estimator. Get transparent pricing in Indian Rupees (INR) for web design, coding, and AI features." 
        keywords="website cost calculator, web development budget estimator, freelancer rates India, website price builder" 
        schema={estimatorSchema}
      />
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-cyan-500 font-mono text-sm mb-4">PRICE CALCULATOR</p>
          <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight mb-6">
            Estimate Your
            <br />
            <span className="text-cyan-500">Project Cost</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Select the services you need to calculate an instant estimated budget and timeline for development.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT - Selection Controls (Bento Span 7) */}
          <div className="lg:col-span-7 space-y-8">
            
            {/* Services Bento Card */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
              <h2 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
                <Calculator className="text-cyan-500" size={20} />
                Select Services
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {servicesOptions.map((opt) => {
                  const isSelected = selectedServices.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => handleServiceToggle(opt.id)}
                      className={`p-5 rounded-xl border text-left transition-all duration-300 relative overflow-hidden group ${
                        isSelected
                          ? 'bg-cyan-500/10 border-cyan-500 shadow-lg shadow-cyan-500/5'
                          : 'bg-black/40 border-white/10 hover:border-cyan-500/30'
                      }`}
                    >
                      {/* Selected Indicator */}
                      {isSelected && (
                        <span className="absolute top-3 right-3 w-5 h-5 bg-cyan-500 text-black rounded-full flex items-center justify-center">
                          <Check size={12} className="stroke-[3]" />
                        </span>
                      )}
                      <h3 className={`font-semibold mb-1 ${isSelected ? 'text-cyan-400' : 'text-white'}`}>{opt.label}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed mb-3">{opt.description}</p>
                      <span className="text-sm font-mono text-cyan-500 font-bold">₹{opt.price.toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Pages & Speed Bento Card */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Pages Slider */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 flex flex-col justify-between">
                <div>
                  <h3 className="font-heading text-xl font-bold mb-2 flex items-center gap-2">
                    <Clock className="text-cyan-500" size={18} />
                    Page Count
                  </h3>
                  <p className="text-gray-400 text-xs mb-6">How many main layout templates does your app need?</p>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between font-mono text-cyan-500 font-bold">
                    <span>Pages:</span>
                    <span>{pages}</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="15"
                    value={pages}
                    onChange={(e) => setPages(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-cyan-500 focus:outline-none"
                  />
                  <div className="flex justify-between text-[10px] text-gray-500 font-mono">
                    <span>1 PAGE</span>
                    <span>15 PAGES</span>
                  </div>
                </div>
              </div>

              {/* Delivery Speed Toggle */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 flex flex-col justify-between">
                <div>
                  <h3 className="font-heading text-xl font-bold mb-2 flex items-center gap-2">
                    <Calendar className="text-cyan-500" size={18} />
                    Timeline Urgency
                  </h3>
                  <p className="text-gray-400 text-xs mb-6">Do you need priority delivery with extra developers?</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsUrgent(false)}
                    className={`flex-1 py-3.5 rounded-xl border font-medium text-sm transition-all ${
                      !isUrgent
                        ? 'bg-cyan-500 text-black border-cyan-500'
                        : 'bg-black/40 border-white/10 text-gray-400 hover:border-cyan-500/30'
                    }`}
                  >
                    Standard
                  </button>
                  <button
                    onClick={() => setIsUrgent(true)}
                    className={`flex-1 py-3.5 rounded-xl border font-medium text-sm transition-all ${
                      isUrgent
                        ? 'bg-cyan-500 text-black border-cyan-500'
                        : 'bg-black/40 border-white/10 text-gray-400 hover:border-cyan-500/30'
                    }`}
                  >
                    Express (-30% Time)
                  </button>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT - Estimated Summary (Bento Span 5) */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="bg-gradient-to-br from-white/5 to-white/[0.01] border border-white/10 rounded-2xl p-8 shadow-2xl relative overflow-hidden group hover:border-cyan-500/30 transition-all duration-500">
              
              {/* Decorative Glow */}
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none transition-all group-hover:bg-cyan-500/20" />

              <h2 className="font-heading text-2xl font-bold mb-8">Estimated Summary</h2>

              {/* Cost Odometer */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <p className="text-gray-400 text-sm mb-1">PROJECT BUDGET</p>
                <div className="flex items-baseline font-heading text-6xl md:text-7xl font-bold tracking-tight text-white">
                  <IndianRupee className="text-cyan-500 mr-1 self-start w-10 h-10 md:w-12 md:h-12" />
                  <AnimatedNumber value={calculateTotal()} />
                </div>
              </div>

              {/* Timeline Indicator */}
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs mb-1">DEVELOPMENT TIMELINE</p>
                  <div className="flex items-center gap-2 text-white font-heading text-2xl font-bold">
                    <Clock className="text-cyan-500" size={20} />
                    <span>~{calculateTime()} Business Days</span>
                  </div>
                </div>
              </div>

              {/* Subscriptions List */}
              <div className="space-y-4 mb-8">
                <p className="text-gray-400 text-xs font-mono">SELECTED CONFIGURATIONS:</p>
                <div className="space-y-2.5 max-h-[140px] overflow-y-auto pr-2">
                  {selectedServices.map(id => {
                    const opt = servicesOptions.find(o => o.id === id);
                    return (
                      <div key={id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-300 font-medium flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                          {opt?.label}
                        </span>
                        <span className="font-mono text-gray-400">₹{opt?.price.toLocaleString()}</span>
                      </div>
                    );
                  })}
                  {pages > 1 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-300 font-medium flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                        {pages - 1} Additional Pages
                      </span>
                      <span className="font-mono text-gray-400">₹{((pages - 1) * 2000).toLocaleString()}</span>
                    </div>
                  )}
                  {isUrgent && (
                    <div className="flex items-center justify-between text-sm text-cyan-400 font-semibold">
                      <span>Express Delivery Fee (+25%)</span>
                      <span className="font-mono">+25%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Booking CTA */}
              <MagneticButton
                onClick={handleBookingRedirect}
                className="w-full py-4 bg-cyan-500 text-black rounded-full font-medium text-lg flex items-center justify-center gap-2 hover:bg-cyan-400 transition-colors"
              >
                Book with Estimate
                <ArrowRight size={18} />
              </MagneticButton>

              <p className="text-center text-gray-500 text-xs mt-4">
                Note: This is an estimated budget. Actual price may vary depending on project complexity.
              </p>
            </div>
          </div>

        </div>

      </div>
    </div>
    </>
  );
};

export default ProjectEstimator;
